import {combine, restore} from 'effector';

import {
    fetchCodeForServiceOrProducts,
    fetchDocumentInvoiceCreate,
    fetchDocumentInvoiceXmlValidation,
    fetchInvoiceIrn,
} from 'api/documents/create';

import {rootDomain} from 'core/rootDomain';
import {
    AllowanceCharge,
    InfoValues,
    InvoiceLine,
    InvoiceLineChargeData,
    PageValues,
    RoundAndPrepayd,
    TaxesAndTotal,
} from 'types/invoices';

import {
    DEFAULT_LINE_VALUES,
    documnetPageValues,
    generalInfoPageValues,
    taxesPageValue,
} from './defaultVariables';

const domain = rootDomain.createDomain('InvoiceCreating');

export const $invoiceCreationPage = domain.createStore<number>(0);
export const $invoiceLineValues = domain.createStore<InvoiceLine[]>([]);
export const $invoiceLinesHolder = domain.createStore<InvoiceLine[]>([]);
export const $invoiceLinesErrors = $invoiceLinesHolder.map((line) =>
    line.reduce<{index: number; fields: string[]}[]>((acc, i, index) => {
        const requiredFields = ['qty', 'price', 'lines', 'vatCategory', 'vat', 'lineAmountWVat'];
        const fields = requiredFields.filter((field) => {
            if (field === 'vatCategory') {
                return !i.vatCategory?.value || i.vatCategory.value === '';
            }
            if (field === 'vat' && i.vatCategory?.value === 'S' && i.vat === '0') {
                return true;
            }
            const value = i[field as keyof InvoiceLine];
            return value === undefined || value === null || value === '';
        });
        if (fields.length > 0) {
            acc.push({index, fields});
        }
        return acc;
    }, []),
);
export const $invoiceLinesHolderCodesTotal = $invoiceLinesHolder.map((line) =>
    line.reduce((acc, i) => {
        if (i.combinedCodes) {
            return acc + i.combinedCodes.length;
        }
        return acc + 1;
    }, 0),
);

export const $invoiceLineChargeData = domain.createStore<InvoiceLineChargeData | null>(null);
export const $pageValues = domain.createStore<PageValues>(documnetPageValues);
export const $infoValues = domain.createStore<InfoValues>(generalInfoPageValues);

export const $taxesAndTotal = domain.createStore<{
    data: TaxesAndTotal;
    allowToSend: boolean;
}>({
    data: taxesPageValue,
    allowToSend: false,
});

export const setInitalLineValuesHolder = domain.createEvent<Partial<InvoiceLine> | null>();
export const $initalLineValuesHolder = domain.createStore<Partial<InvoiceLine> | null>(
    DEFAULT_LINE_VALUES,
);

export const invoiceCreationPage = domain.createEvent<number>();
export const goNextPage = domain.createEvent();
export const goPrevPage = domain.createEvent();
export const guarderFetchInvoice = domain.createEvent();
export const updatePageValues = domain.createEvent<PageValues>();
export const updateInfoValues = domain.createEvent<InfoValues>();
export const updateInfoValuesCityAndZip = domain.createEvent<{
    customerZip: string;
    customerCity: string;
    customerCountry: string;
    customerStreet: string;
}>();
export const updateInfoValuesPaymentMean = domain.createEvent<boolean>();

export const updateCustomerCompanyNameOrTIN = domain.createEvent<{value: string; label: string}>();
export const updateIRN = domain.createEvent<string>();
export const setInvoiceLinesHolder = domain.createEvent<InvoiceLine[]>();
export const updateInvoiceLinesHolder = domain.createEvent<{
    index: number;
    data:
        | {columnId: string; value: string | string[] | null}
        | {columnId: string; value: string | string[] | null}[];
}>();
export const removeInvoiceLinesHolder = domain.createEvent<number>();
export const updateAllDataFromInvoiceLinesHolder = domain.createEvent<InvoiceLine[]>();
export const copyInvoiceLineData = domain.createEvent<{
    index: number;
    initialLineData?: Partial<InvoiceLine>;
}>();

export const addInvoiceLineValues = domain.createEvent<InvoiceLine[]>();

export const setInvoiceLineChargeData = domain.createEvent<InvoiceLineChargeData | null>();

export const removeInvoiceLineValues = domain.createEvent<number>();
export const updateTaxesAndTotal = domain.createEvent<{
    data: Partial<TaxesAndTotal>;
    allowToSend: boolean;
}>();
export const setTaxesAndTotalFromXml = domain.createEvent<{
    data: TaxesAndTotal;
    allowToSend: boolean;
}>();
export const updateTaxesAndTotalFromInvoiceLines = domain.createEvent<{
    data: TaxesAndTotal;
    allowToSend: boolean;
}>();
export const updateTaxesAndTotalRoundAndPrepayd = domain.createEvent<RoundAndPrepayd>();
export const updateTaxesAndTotalAllowanceCharge = domain.createEvent<AllowanceCharge>();
export const removeTaxesAndTotalAllowanceCharge = domain.createEvent<number>();

export const $validationErrorToPassNext = domain.createStore<boolean>(false);
export const handleValidationErrorToPassNext = domain.createEvent<boolean>();

export const setInvoiceCreatingPending = domain.createEvent<boolean>();

export const fetchXmlValidationFx = domain.createEffect({
    name: 'fetchXmlValidationFx',
    handler: fetchDocumentInvoiceXmlValidation,
});

export const fetchInvocieXmlFx = domain.createEffect({
    name: 'fetchInvocieXmlFx',
    handler: fetchDocumentInvoiceCreate,
});

export const fetchInvoiceIrnFx = domain.createEffect({
    name: 'fetchInvoiceIrnFx',
    handler: fetchInvoiceIrn,
});

export const fetchCodeForServiceOrProductsFx = domain.createEffect({
    name: 'fetchCodeForServiceOrProductsFx',
    handler: fetchCodeForServiceOrProducts,
});

export const arrStores = combine([
    $infoValues,
    $pageValues,
    $invoiceLineChargeData,
    $invoiceLineValues,
    $taxesAndTotal,
    $invoiceLinesHolder,
    $invoiceLinesErrors,
]);

export const $invoiceCreatingPending = combine(
    fetchXmlValidationFx.pending,
    fetchInvocieXmlFx.pending,
    (fetchXmlValidationFx, fetchInvocieXmlFx) => fetchXmlValidationFx || fetchInvocieXmlFx,
);

export const setShowGroupLinesByGtinModal = domain.createEvent<boolean>();
export const $showGroupLinesByGtinModal = restore(setShowGroupLinesByGtinModal, false);
export const setForceAllowToSend = domain.createEvent<boolean>();
export const $forceAllowToSend = restore(setForceAllowToSend, false);
